import React, { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  doc, 
  getDoc,
  Firestore 
} from 'firebase/firestore';
import { Functions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../../hooks/AuthProvider';
import { MetadataField, App } from './AppManager';
import { LOCATION_OPTIONS } from './config/locationConfig';

// Available app types
const AVAILABLE_TYPES = [
  "single-store",
  "single-store-multi-location",
  "multi-store"
];

interface MetadataCollection {
  permissions: {
    read: string | string[];
    write: string | string[];
  };
  fields: MetadataField[];
}

interface AppListProps {
  db: Firestore;
  functions: Functions;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
  setLoading: (loading: boolean) => void;
  onSelectApp: (appId: string) => void;
  canApproveDeployment: boolean;
  onViewAdminPanel?: () => void;
  selectedOrganizationId: string;
  onBackToOrganizations: () => void;
  onViewPermissions?: () => void;
}

const AppList: React.FC<AppListProps> = ({ 
  db, 
  functions, 
  onError, 
  onSuccess, 
  setLoading,
  onSelectApp,
  canApproveDeployment,
  onViewAdminPanel,
  selectedOrganizationId,
  onBackToOrganizations,
  onViewPermissions
}) => {
  const { currentUser, userPermissions } = useAuth();
  const [apps, setApps] = useState<MetadataField[]>([]);
  const [newAppName, setNewAppName] = useState<string>('');
  const [newAppId, setNewAppId] = useState<string>('');
  const [newAppType, setNewAppType] = useState<string>('single-store');
  const [newAppRegion, setNewAppRegion] = useState<string>('europe-west2');
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [showNewAppForm, setShowNewAppForm] = useState<boolean>(false);
  
  // Cloud function references
  const createAppFunc = httpsCallable(functions, 'createApp');
  
  // Check if user can create apps for this organization
  const canCreateApps = (): boolean => {
    return userPermissions.some(permission => 
      permission === `organizations_${selectedOrganizationId}_write` ||
      permission === `organizations_${selectedOrganizationId}_admin`
    );
  };

  // Check if user has admin access to this organization
  const hasAdminAccess = (): boolean => {
    return userPermissions.some(permission => 
      permission === `organizations_${selectedOrganizationId}_admin`
    );
  };

  // Get organization name (you might want to fetch this from Firestore)
  const getOrganizationName = (): string => {
    // For now, return the ID. You might want to fetch actual names from Firestore
    return selectedOrganizationId;
  };
  
  // Generate the App ID prefix based on current year
  const getCurrentYear = () => {
    return new Date().getFullYear();
  };
  
  const generateAppIdPrefix = () => {
    return `dp-${getCurrentYear()}-`;
  };

  // Update custom ID when app name changes
  useEffect(() => {
    if (newAppName) {
      const baseId = newAppName
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-');
      
      setNewAppId(baseId);
    } else {
      setNewAppId('');
    }
  }, [newAppName]);

  // Update region when location changes
  useEffect(() => {
    const location = LOCATION_OPTIONS.find(loc => loc.id === selectedLocation);
    if (location) {
      setNewAppRegion(location.appRegion);
    }
  }, [selectedLocation]);
  
  // Load apps from metadata (filtered by organization)
  useEffect(() => {
    const loadApps = async () => {
      if (!currentUser || !selectedOrganizationId) return;
      
      setLoading(true);
      try {
        const metadataRef = doc(db, 'metadata', 'apps');
        const metadataSnapshot = await getDoc(metadataRef);
        
        if (metadataSnapshot.exists()) {
          const metadataData = metadataSnapshot.data() as MetadataCollection;
          const allApps = metadataData.fields || [];
          
          // Filter apps by organization
          // Assuming apps have an organizationId field or we can derive it from their structure
          const orgApps = allApps.filter(app => {
            // You'll need to determine how to identify which organization an app belongs to
            // This could be stored as a field in the app metadata or derived from the app ID
            // For now, I'm assuming there's an organizationId field
            return (app as any).organizationId === selectedOrganizationId;
          });
          
          setApps(orgApps);
        } else {
          setApps([]);
        }
        
      } catch (err) {
        console.error('Error loading apps:', err);
        onError('Failed to load apps. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    loadApps();
  }, [db, currentUser, selectedOrganizationId, setLoading, onError]);

  // Create new app (using cloud function)
  const handleCreateApp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!canCreateApps()) {
      onError('You do not have permission to create apps for this organization.');
      return;
    }
    
    if (!newAppName.trim()) {
      onError('App name is required.');
      return;
    }
    
    if (!newAppId.trim()) {
      onError('App ID is required.');
      return;
    }
    
    setLoading(true);
    try {
      // Generate prefixed ID
      const prefixedId = generateAppIdPrefix() + newAppId;
      
      // Check if ID is already in use
      const existingApp = apps.find(app => app.id === prefixedId);
      if (existingApp) {
        onError(`An app with this ID already exists. Please use a different name or ID.`);
        setLoading(false);
        return;
      }
      
      // Create app data object to be sent to the cloud function
      const appData: App = {
        id: prefixedId,
        name: newAppName,
        description: "",
        enabled: true,
        icon: "app-default",
        url: "",
        requiredPermission: "USER",
        region: newAppRegion,
        type: newAppType,
        status: "registered",
        deploymentApproved: false,
        initializationComplete: false,
        adminSetupComplete: false,
        locationId: selectedLocation,
        organizationId: selectedOrganizationId, // Set the organization ID
        theme: {
          variant: 'modern-light',
          accentColor: '#4169e1'
        },
        themeConfigured: false
      };
      
      // Call the cloud function to create the app
      const result = await createAppFunc({ appData });
      
      // @ts-ignore (result data structure)
      if (result.data && result.data.success) {
        // Successfully created the app
        
        // Update the apps array (metadata) with the new app
        const updatedApps = [
          ...apps,
          { id: prefixedId, name: newAppName, enabled: true, organizationId: selectedOrganizationId }
        ];
        setApps(updatedApps);
        
        // Success message
        // @ts-ignore
        onSuccess(result.data.message || `App "${newAppName}" created successfully.`);
        
        // Reset form
        setNewAppName('');
        setNewAppId('');
        setNewAppType('single-store');
        setNewAppRegion('europe-west2');
        setSelectedLocation('');
        setShowNewAppForm(false);
        
        // Select the newly created app to view its workflow
        onSelectApp(prefixedId);
      } else {
        // @ts-ignore
        onError(result.data?.message || 'Failed to create app. Please try again.');
      }
      
    } catch (err: any) {
      console.error('Error creating app:', err);
      onError(err.message || 'Failed to create app. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="applist-content-header">
        <h1 className="applist-page-title">App Management</h1>
        <div className="applist-header-actions">
          {canCreateApps() && (
            <button
              type="button"
              className="applist-add-app-btn"
              onClick={() => setShowNewAppForm(!showNewAppForm)}
            >
              {showNewAppForm ? 'Cancel' : '+ Add App'}
            </button>
          )}
        </div>
      </div>

      <div className="applist-content-body">
        {showNewAppForm && canCreateApps() && (
          <div className="applist-new-app-form">
            <h3>Create New App</h3>
            <form onSubmit={handleCreateApp}>
              <div className="applist-form-field">
                <label>App Name</label>
                <input
                  type="text"
                  value={newAppName}
                  onChange={(e) => setNewAppName(e.target.value)}
                  placeholder="Enter app name"
                  required
                />
              </div>
              <div className="applist-form-field">
                <label>App ID</label>
                <div className="applist-id-input-group">
                  <span className="applist-id-prefix">{generateAppIdPrefix()}</span>
                  <input
                    type="text"
                    value={newAppId}
                    onChange={(e) => setNewAppId(e.target.value)}
                    placeholder="Enter custom ID suffix"
                    className="applist-id-suffix-input"
                    required
                  />
                </div>
                <small className="applist-form-help-text">
                  The app ID must match your Firebase project ID. Make sure this ID is available in Firebase.
                </small>
              </div>
              <div className="applist-form-field">
                <label>Location</label>
                <select
                  className="applist-location-select"
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  required
                >
                  <option value="">Select a location</option>
                  {LOCATION_OPTIONS
                    .filter(location => location.organizationId === selectedOrganizationId)
                    .map(location => (
                      <option key={location.id} value={location.id}>
                        {location.locationName}, {location.country} ({location.currencyCode})
                      </option>
                    ))}
                </select>
                <small className="applist-form-help-text">
                  This will determine the region, currency, and other location-specific settings.
                </small>
              </div>
              <div className="applist-form-field">
                <label>App Type</label>
                <select
                  className="applist-type-select"
                  value={newAppType}
                  onChange={(e) => setNewAppType(e.target.value)}
                >
                  {AVAILABLE_TYPES.map(type => (
                    <option key={type} value={type}>
                      {type.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </option>
                  ))}
                </select>
              </div>
              <div className="applist-form-actions">
                <button 
                  type="submit" 
                  className="applist-submit-btn"
                  disabled={!newAppName.trim() || !newAppId.trim() || !selectedLocation}
                >
                  Create App
                </button>
                <button 
                  type="button" 
                  className="applist-cancel-btn"
                  onClick={() => setShowNewAppForm(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
        
        <div className="applist-app-cards">
          {apps.length > 0 ? (
            apps.map(app => (
              <div
                key={app.id}
                className="applist-app-card"
              >
                <div className="applist-card-content">
                  <h3 className="applist-card-title">{app.name}</h3>
                  <div className="applist-card-status">
                    <span className={`applist-status-indicator ${app.enabled ? 'applist-status-enabled' : 'applist-status-disabled'}`}>
                      {app.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <div className="applist-card-id">
                    ID: {app.id}
                  </div>
                  <div className="applist-card-footer">
                    <button 
                      type="button"
                      className="applist-view-workflow-btn" 
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectApp(app.id);
                      }}
                    >
                      View
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="applist-empty-state">
              <p>No apps available for this organization. {canCreateApps() ? 'Create your first app to get started.' : 'Contact an administrator to create apps.'}</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AppList;